import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { toast } from "sonner";

import { authApi, authQueryKeys, useUserActions } from "@/modules/auth";
import type { DialogComponentProps } from "@/shared/components/dialog";
import { Button } from "@/shared/ui/button";
import { DialogFooter } from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";

type AccountAction = "nickname" | "email" | "phone" | "password";

interface AccountActionDialogProps extends DialogComponentProps {
  action: AccountAction;
  currentValue?: string;
  hasPassword?: boolean;
}

const PHONE_PATTERN = /^1[3-9]\d{9}$/;

export function AccountActionDialog({ close, resolve, action, currentValue = "", hasPassword = false }: AccountActionDialogProps) {
  const queryClient = useQueryClient();
  const { setToken } = useUserActions();
  const inputId = useId();
  const codeId = useId();
  const confirmId = useId();
  const [value, setValue] = useState(action === "nickname" ? currentValue : "");
  const [code, setCode] = useState("");
  const [oldPassword, setOldPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState("");

  async function sendCode() {
    const phone = value.trim();
    if (!PHONE_PATTERN.test(phone)) {
      setError("请输入 11 位有效手机号。");
      return;
    }
    setError("");
    setSendingCode(true);
    try {
      await authApi.sendPhoneVerifySMS({ phone });
      setCodeSent(true);
      toast.success("验证码已发送");
    } catch {
      // 请求层展示接口错误。
    } finally {
      setSendingCode(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (action === "nickname") return;
    const trimmed = value.trim();
    if (action === "email" && (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed))) {
      setError("请输入有效的新邮箱地址。");
      return;
    }
    if (action === "email" && trimmed === currentValue) {
      setError("新邮箱与当前邮箱相同。");
      return;
    }
    if (action === "phone" && (!PHONE_PATTERN.test(trimmed) || !code.trim())) {
      setError("请输入有效手机号和短信验证码。");
      return;
    }
    if (action === "password") {
      if (hasPassword && !oldPassword) {
        setError("请输入当前密码。");
        return;
      }
      if (value.length < 6) {
        setError("新密码至少需要 6 位。");
        return;
      }
      if (value !== confirmation) {
        setError("两次输入的密码不一致。");
        return;
      }
    }
    setError("");
    setBusy(true);
    try {
      if (action === "email") {
        await authApi.sendChangeEmailEmail({ new_email: trimmed });
        toast.success("确认邮件已发送，请在 10 分钟内打开新邮箱中的链接");
      } else if (action === "phone") {
        await authApi.changePhoneByCode({ new_phone: trimmed, code: code.trim() });
        await queryClient.invalidateQueries({ queryKey: authQueryKeys.userInfo });
        toast.success(currentValue ? "手机号已更换" : "手机号已绑定");
      } else {
        const response = hasPassword
          ? await authApi.updatePassword({ old_password: oldPassword, new_password: value })
          : await authApi.setNewPassword({ new_password: value });
        setToken(response.token);
        await queryClient.invalidateQueries({ queryKey: authQueryKeys.userInfo });
        toast.success(hasPassword ? "密码已修改" : "密码已设置");
      }
      resolve();
    } catch {
      // 请求层展示接口错误。
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="space-y-4" onSubmit={(event) => void submit(event)} autoComplete="off">
      {action === "nickname" && <p className="text-xs text-muted-foreground">昵称修改接口尚未开放，暂时无法保存更改。</p>}
      {action === "password" && hasPassword && (
        <div className="space-y-2">
          <Label htmlFor={codeId} className="text-xs text-muted-foreground">当前密码</Label>
          <Input id={codeId} type="password" autoComplete="off" value={oldPassword} onChange={(event) => setOldPassword(event.target.value)} />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={inputId} className="text-xs text-muted-foreground">
          {action === "nickname" ? "昵称" : action === "email" ? "新邮箱" : action === "phone" ? "新手机号" : "新密码"}
        </Label>
        <Input
          id={inputId}
          type={action === "password" ? "password" : action === "email" ? "email" : "text"}
          inputMode={action === "phone" ? "tel" : undefined}
          autoComplete="off"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          disabled={busy || action === "nickname"}
        />
      </div>
      {action === "phone" && (
        <div className="space-y-2">
          <Label htmlFor={codeId} className="text-xs text-muted-foreground">短信验证码</Label>
          <div className="flex gap-2">
            <Input id={codeId} inputMode="numeric" autoComplete="off" value={code} onChange={(event) => setCode(event.target.value)} disabled={busy} />
            <Button type="button" variant="outline" className="shrink-0 font-normal" disabled={busy || sendingCode} onClick={() => void sendCode()}>
              {sendingCode && <Loader2 className="size-4 animate-spin" />}
              {codeSent ? "重新发送" : "发送验证码"}
            </Button>
          </div>
        </div>
      )}
      {action === "password" && (
        <div className="space-y-2">
          <Label htmlFor={confirmId} className="text-xs text-muted-foreground">确认新密码</Label>
          <Input id={confirmId} type="password" autoComplete="off" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
        </div>
      )}
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={close} disabled={busy}>取消</Button>
        <Button type="submit" disabled={busy || action === "nickname"}>
          {busy && <Loader2 className="size-4 animate-spin" />}
          {action === "email" ? "发送确认邮件" : "确认"}
        </Button>
      </DialogFooter>
    </form>
  );
}
